/**
 * Executes the whitelisted UI actions the onboarding agent can request via
 * Gemini Live tool-calls: reading what's actually on the current screen,
 * navigating to a known screen, switching in-meeting tabs, and highlighting an on-screen
 * control by its visible label. All degrade gracefully — a bad target never throws,
 * it just reports "not found" back to the model.
 */

import { useLiveMeetingStore } from "../meeting/live/store.ts";
import { NAV_TARGETS, screenContextFor } from "./screenContext.ts";

export type NavigateFn = (path: string) => void;

export type MeetingPanelTab =
  | "infographic"
  | "diagram"
  | "decisiontree"
  | "whatsnew"
  | "usecase"
  | "transcript"
  | "captions";

export const MEETING_TABS: Record<string, MeetingPanelTab> = {
  infographic: "infographic",
  diagram: "diagram",
  architecture: "diagram",
  "architecture diagram": "diagram",
  decisiontree: "decisiontree",
  "decision tree": "decisiontree",
  flowchart: "decisiontree",
  whatsnew: "whatsnew",
  "what's new": "whatsnew",
  "whats new": "whatsnew",
  usecase: "usecase",
  usecases: "usecase",
  "use cases": "usecase",
  transcript: "transcript",
  captions: "captions",
  subtitles: "captions",
};

/** Switch between tabs in the live meeting right panel. Returns a status string. */
export function executeSwitchMeetingTab(tab: string): string {
  const norm = (tab || "").toLowerCase().trim();
  const matched = MEETING_TABS[norm];
  if (!matched) {
    return `Unknown meeting tab "${tab}". Valid tabs: infographic, diagram, decisiontree, whatsnew, usecase, transcript, captions.`;
  }
  if (!window.location.pathname.includes("/live")) {
    return `"${tab}" is a tab on the live meeting screen. Start or open a meeting to view it.`;
  }
  useLiveMeetingStore.getState().setRightPanelTab(matched);
  return `Switched to the "${matched}" tab.`;
}

/** Navigate to a whitelisted screen key (see NAV_TARGETS) or switch meeting tab. Returns a status string. */
export function executeNavigate(to: string, navigate: NavigateFn): string {
  const key = (to || "").toLowerCase().trim();

  // If the target is an in-meeting tab and we are on a live meeting screen, switch tab!
  if (window.location.pathname.includes("/live") && MEETING_TABS[key]) {
    return executeSwitchMeetingTab(key);
  }

  const path = NAV_TARGETS[key];
  if (!path) {
    if (MEETING_TABS[key]) {
      return `"${to}" is a tab on the live meeting screen. Start or open a meeting to view it.`;
    }
    return `No such screen "${to}". Valid screens: ${Object.keys(NAV_TARGETS).join(", ")}.`;
  }
  navigate(path);
  return `Navigated to ${key}.`;
}

let clearTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Highlight the first visible button/link whose label contains `text`
 * (case-insensitive). Scrolls it into view and adds a pulsing outline that
 * auto-clears. Returns whether a match was found.
 */
export function executeHighlight(text: string): string {
  const needle = (text || "").toLowerCase().trim();
  if (!needle) return "No text provided to highlight.";

  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>("button, a, [role='button'], [role='tab'], label"),
  );
  const match = candidates.find((el) => {
    if (el.offsetParent === null) return false; // not visible
    const label = (el.textContent || el.getAttribute("aria-label") || el.title || "").toLowerCase();
    return label.includes(needle);
  });

  if (!match) return `Could not find a visible control labelled "${text}" on this screen.`;

  clearHighlight();
  match.classList.add("oa-highlight");
  match.scrollIntoView({ behavior: "smooth", block: "center" });
  clearTimer = setTimeout(() => {
    match.classList.remove("oa-highlight");
    clearTimer = null;
  }, 6000);
  return `Highlighted "${match.textContent?.trim() || text}".`;
}

export function clearHighlight(): void {
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
  for (const el of Array.from(document.querySelectorAll(".oa-highlight"))) {
    el.classList.remove("oa-highlight");
  }
}

// ---- Hover tracking: what control is under the user's cursor right now --------

let hoverLabel = "";
function labelOf(el: Element | null): string {
  if (!el) return "";
  const ctrl = el.closest(
    "button, a, [role='button'], [role='tab'], label, input, select, textarea",
  );
  if (!ctrl) return "";
  const text = (
    (ctrl as HTMLElement).innerText ||
    ctrl.getAttribute("aria-label") ||
    (ctrl as HTMLElement).title ||
    (ctrl as HTMLInputElement).placeholder ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim();
  return text.slice(0, 60);
}
function onMouseMove(e: MouseEvent): void {
  hoverLabel = labelOf(e.target as Element);
}

/** Start/stop tracking the hovered control (called when the agent panel opens/closes). */
export function startHoverTracking(): void {
  window.addEventListener("mousemove", onMouseMove, { passive: true });
}
export function stopHoverTracking(): void {
  window.removeEventListener("mousemove", onMouseMove);
  hoverLabel = "";
}

/**
 * Snapshot of what is ACTUALLY on the screen right now — the route, headings,
 * the visible clickable controls, and what the user is hovering. The agent calls
 * this before giving directions so it grounds its guidance in the real UI instead
 * of guessing. Returns a compact text block.
 */
/** Find the top-most visible modal/popup/overlay currently open (if any). */
function openModal(): HTMLElement | null {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(
      "[role='dialog'], [aria-modal='true'], dialog[open], .modal-back, .modal, .lightbox-overlay, [class*='modal'], [class*='popup'], [class*='drawer']",
    ),
  ).filter(
    (el) =>
      el.offsetParent !== null &&
      !el.closest(".oa-panel") &&
      el.getBoundingClientRect().width > 120,
  );
  // The last one in DOM order is usually the top-most (highest stacking).
  return nodes.length ? nodes[nodes.length - 1]! : null;
}

function controlsIn(root: ParentNode, cap = 40): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const el of Array.from(
    root.querySelectorAll<HTMLElement>("button, a, [role='button'], [role='tab'], select, input"),
  )) {
    if (el.offsetParent === null || el.closest(".oa-panel")) continue;
    const label = (
      el.innerText ||
      el.getAttribute("aria-label") ||
      el.title ||
      (el as HTMLInputElement).placeholder ||
      ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (!label || label.length > 50 || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push(label);
    if (out.length >= cap) break;
  }
  return out;
}

export function readScreen(): string {
  const path = window.location.pathname;
  const ctx = screenContextFor(path);

  const headings = Array.from(document.querySelectorAll<HTMLElement>("h1, h2, h3"))
    .filter((el) => el.offsetParent !== null && el.innerText.trim() && !el.closest(".oa-panel"))
    .map((el) => el.innerText.replace(/\s+/g, " ").trim())
    .slice(0, 10);

  const lines = [
    `Current screen: ${ctx.title} (path ${path}).`,
    headings.length ? `Headings: ${headings.join(" | ")}.` : "",
    (() => {
      const c = controlsIn(document.body);
      return c.length ? `Clickable controls visible: ${c.join(", ")}.` : "";
    })(),
  ];

  // If a dialog/popup is open, describe it explicitly — this is what the user is
  // most likely asking about, and it changes in real time.
  const modal = openModal();
  if (modal) {
    const mTitle = modal
      .querySelector<HTMLElement>("h1, h2, h3, [class*='title'], [class*='head']")
      ?.innerText.replace(/\s+/g, " ")
      .trim();
    const mText = (modal.innerText || "").replace(/\s+/g, " ").trim().slice(0, 600);
    const mControls = controlsIn(modal, 25);
    lines.push(
      `⚠️ A POPUP/DIALOG IS CURRENTLY OPEN${mTitle ? ` — "${mTitle}"` : ""}. This is what the user is looking at right now.`,
    );
    if (mControls.length) lines.push(`Popup controls: ${mControls.join(", ")}.`);
    if (mText) lines.push(`Popup content: ${mText}`);
  }

  if (hoverLabel) lines.push(`The user is currently hovering: "${hoverLabel}".`);

  return lines.filter(Boolean).join("\n");
}
